'use client'

import { useState } from 'react'
import { Clock, MailCheck, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useAuth } from '@/components/AuthProvider'
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog'

/**
 * Full-screen gate shown to a demo account that has registered but is not yet
 * approved (pending_approval) or was rejected. Replaces the whole app so a
 * pending/rejected user can't reach any data surface. The server enforces the
 * same state independently — this is the friendly UX layer.
 */
export function PendingApprovalScreen({ rejected = false }: { rejected?: boolean }) {
  const { signOut } = useAuth()
  const [deleteOpen, setDeleteOpen] = useState(false)

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src="/zerosnag-blue.png" alt="ZeroSnag" width={160} height={48} className="h-12 w-auto" />
          </div>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {rejected ? (
            <>
              <div className="flex justify-center">
                <div className="rounded-full bg-red-50 p-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
              </div>
              <h1 className="text-xl font-semibold text-[#001c4d]">Zugang nicht freigegeben</h1>
              <p className="text-gray-600">
                Ihr Demo-Zugang wurde leider nicht freigeschaltet. Bei Fragen erreichen Sie uns
                unter <a className="text-[#001c4d] underline" href="mailto:info@zerosnag.ai">info@zerosnag.ai</a>.
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center">
                <div className="rounded-full bg-amber-50 p-4">
                  <Clock className="w-10 h-10 text-amber-500" />
                </div>
              </div>
              <h1 className="text-xl font-semibold text-[#001c4d]">Ihr Zugang wird geprüft</h1>
              <p className="text-gray-600">
                Vielen Dank für Ihre Registrierung! Wir prüfen Ihre Anfrage und schalten Ihren
                Demo-Zugang in Kürze manuell frei. Sie erhalten eine E-Mail, sobald es losgehen kann.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg py-3 px-4">
                <MailCheck className="w-4 h-4 flex-none" />
                <span>Bitte bestätigen Sie auch Ihre E-Mail-Adresse, falls noch nicht geschehen.</span>
              </div>
            </>
          )}

          <Button variant="outline" className="w-full mt-2" onClick={() => void signOut()}>
            Abmelden
          </Button>
          <button
            onClick={() => setDeleteOpen(true)}
            className="text-xs text-gray-400 hover:text-red-600 transition-colors mt-1"
          >
            Konto löschen
          </button>
        </CardContent>
      </Card>
      <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </div>
  )
}
